import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import _toNumber from "lodash/toNumber";
import { useState, useCallback, useEffect } from "react";
import { useJoinBrandClubWithRedirect } from "../../utils/hooks/useJoinBrandClub.js";
import { FilterDialog } from "../BrandclubNavigation/FilterDialog.js";
import ProductBrandCard from "../components/BrandclubProductCard/ProductBrandCard.js";
import { GenericContainer } from "../components/GenericContainer.js";
import { StoreBrandingType } from "../type.js";
import SearchProductGrid from "./SearchProductGrid.js";
import { searchParamsToSearchFilters } from "./searchResultsUtils.js";
import { searchBrandClubsForUserV2, getUnauthedSearchBrandClubs } from "../../api/graphQl/brand.js";
import { Button } from "../../Components/Button/Button.js";
import { UNAUTHENTICATED_SEARCH_PRODUCTS, ProductOperation } from "../../api/graphQl/product.js";
import { WIDGET_SPACE_HALF, WIDGET_SPACE, TABLET_SCREEN_SIZE, MOBILE_HORIZONTAL_PADDING, TABLET_HORIZONTAL_PADDING, MOBILE_SCREEN_SIZE, DESKTOP_SCREEN_SIZE, DESKTOP_HORIZONTAL_PADDING } from "../../utils/constants/Constants.js";
const PAGE_SIZE = 60;
const SearchResultsContainer = styled(GenericContainer)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: 30,
  paddingBottom: WIDGET_SPACE,
  paddingLeft: "5%",
  paddingRight: "5%",
  boxSizing: "border-box",
  color: theme.vars.palette.text.primary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    backgroundColor: "#fff",
    padding: `${WIDGET_SPACE_HALF}px ${0}px`
  }
}));
const ContentWrapper = styled(Grid)({
  maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
  width: "100%",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: `${0}px ${TABLET_HORIZONTAL_PADDING}px`
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: `${0}px ${MOBILE_HORIZONTAL_PADDING}px`
  }
});
const findMostCommonBrandId = (searchProducts) => {
  const brandCounts = /* @__PURE__ */ new Map();
  let mostCommonBrandId = void 0;
  let maxCount = 0;
  for (const product of searchProducts) {
    const count = (brandCounts.get(product.brandId) || 0) + 1;
    brandCounts.set(product.brandId, count);
    if (count >= maxCount) {
      maxCount = count;
      mostCommonBrandId = product.brandId;
    }
  }
  return mostCommonBrandId;
};
const SearchHeader = styled(Grid)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  color: theme.vars.palette.text.primary,
  marginBottom: 38,
  marginTop: 30,
  ".headerText": {
    fontSize: 16,
    fontWeight: 600
  }
}));
const PaginationContainer = styled("div")({
  textAlign: "right",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 20
  }
});
const SearchResults = ({
  LinkComponent,
  apolloClient,
  useSearchParams,
  pageContext,
  onShareClick,
  handleJoinClub,
  useNavigate,
  appConfig,
  ...props
}) => {
  var _a, _b, _c;
  const [popularProducts, setPopularProducts] = useState([]);
  const navigate = useNavigate();
  const locationPathname = window.location.pathname;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [totalResultCount, setTotalResultCount] = useState(0);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [mostPopularBrand, setMostPopularBrand] = useState(void 0);
  const [facets, setFacets] = useState(null);
  const searchRequestBody = searchParamsToSearchFilters(searchParams, pageContext);
  const searchTerms = (_a = searchRequestBody == null ? void 0 : searchRequestBody.titleInclude) == null ? void 0 : _a.join(" ");
  const searchText = searchTerms ?? "All products";
  const [searchInputText, setSearchInputText] = useState(searchTerms);
  const isSignedIn = !!props.userProfile;
  const { joinedState, handleJoin, campaignRewards } = useJoinBrandClubWithRedirect({
    brand: mostPopularBrand,
    handleJoinClub,
    navigate
  });
  const fetchBrandclub = useCallback(
    async (brandIds = [], searchTerm) => {
      var _a2, _b2, _c2;
      let titleInclude = void 0;
      if (searchTerm && !brandIds.length) {
        titleInclude = [searchTerm];
      }
      try {
        const RES_KEY = isSignedIn ? "SearchBrandClubsForUserV2" : "UnauthedSearchBrandClubs";
        const query = isSignedIn ? searchBrandClubsForUserV2 : getUnauthedSearchBrandClubs;
        const res = await apolloClient.query({
          query,
          variables: {
            page: {
              start: 0,
              size: 1
            },
            titleInclude,
            brandIds,
            searchIntent: "InAppSearch"
          },
          fetchPolicy: "network-only"
        });
        setMostPopularBrand((_c2 = (_b2 = (_a2 = res == null ? void 0 : res.data) == null ? void 0 : _a2[RES_KEY]) == null ? void 0 : _b2.items) == null ? void 0 : _c2[0]);
      } catch (e) {
        console.error(e);
        return void 0;
      }
    },
    [isSignedIn]
  );
  useEffect(() => {
    setSearchInputText(searchTerms);
  }, [searchTerms]);
  useEffect(() => {
    setFilterDialogOpen(false);
  }, [locationPathname]);
  const page = _toNumber(searchParams.get("page"));
  const movePrev = () => {
    searchParams.set("page", `${page - PAGE_SIZE}`);
    window.scrollTo({ top: 0, behavior: "auto" });
    setSearchParams(searchParams);
  };
  const moveNext = () => {
    searchParams.set("page", `${page + PAGE_SIZE}`);
    window.scrollTo({ top: 0, behavior: "auto" });
    setSearchParams(searchParams);
  };
  useEffect(() => {
    if (apolloClient) {
      const fetchProducts = async () => {
        var _a2, _b2, _c2, _d, _e, _f;
        setLoading(true);
        const treatment = (pageContext == null ? void 0 : pageContext.abTestingTreatments) && (pageContext == null ? void 0 : pageContext.abTestingTreatments.searchImprovementTreatment) ? {
          search: pageContext == null ? void 0 : pageContext.abTestingTreatments.searchImprovementTreatment
        } : void 0;
        const { data } = await apolloClient.query({
          query: UNAUTHENTICATED_SEARCH_PRODUCTS,
          variables: {
            ...searchRequestBody,
            page: {
              start: page,
              size: PAGE_SIZE
            },
            computeFacets: true,
            facetFieldNames: ["categoryId", "retailerId", "subCategoryId", "brandId"],
            treatment
          }
        });
        if (page === 0 && ((_a2 = appConfig == null ? void 0 : appConfig.domainConfig) == null ? void 0 : _a2.storeBrandingType) === StoreBrandingType.UniversalStore) {
          const mostPopularBrandId = findMostCommonBrandId(
            // The brandclub app only fetches 20 products at a time and only use those 20 to calculate the most popular brand.
            // To ensure we display the same results even though we fetch more products, we only use the first 20 products and only on the first page.
            // See https://gitlab.com/stackline-dev/brandclub-mobile-app/-/blob/5e55fd3d9da2211dbb7194745e8aa87da9e4606c/src/screens/Shop/SearchResultsPage.tsx#L203 for reference
            (_c2 = (_b2 = data == null ? void 0 : data[ProductOperation.UnauthenticatedSearch]) == null ? void 0 : _b2.products) == null ? void 0 : _c2.slice(0, 20)
          );
          await fetchBrandclub(mostPopularBrandId != null ? [mostPopularBrandId] : void 0, searchTerms);
        }
        setTotalResultCount(((_d = data == null ? void 0 : data[ProductOperation.UnauthenticatedSearch]) == null ? void 0 : _d.totalProducts) ?? 0);
        setPopularProducts(((_e = data == null ? void 0 : data[ProductOperation.UnauthenticatedSearch]) == null ? void 0 : _e.products) ?? []);
        setFacets(((_f = data == null ? void 0 : data[ProductOperation.UnauthenticatedSearch]) == null ? void 0 : _f.facets) ?? null);
        setLoading(false);
      };
      fetchProducts();
    }
  }, [apolloClient, appConfig, searchParams, fetchBrandclub]);
  const hasResults = !loading && popularProducts.length;
  const disableBack = page === 0;
  const totalPages = Math.ceil(totalResultCount / PAGE_SIZE);
  const disableNext = page + 1 >= totalPages;
  return /* @__PURE__ */ jsx(SearchResultsContainer, { children: /* @__PURE__ */ jsxs(ContentWrapper, { children: [
    /* @__PURE__ */ jsx(
      FilterDialog,
      {
        searchInput: searchInputText,
        setSearchInput: setSearchInputText,
        brandId: (_b = props.brandEntity) == null ? void 0 : _b.id,
        routePrefix: props.routePrefix,
        navigate,
        open: filterDialogOpen,
        onClose: () => setFilterDialogOpen(false),
        persistentQueryParams: props.persistentQueryParams,
        searchParams,
        pageContext,
        axios: props.axios,
        facets
      }
    ),
    /* @__PURE__ */ jsx(SearchHeader, { container: true, wrap: "nowrap", gap: 1, children: loading ? /* @__PURE__ */ jsxs("div", { className: "headerText", children: [
      " ",
      /* @__PURE__ */ jsx(
        Typography,
        {
          component: "h2",
          fontSize: 24,
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          sx: {
            [`@media screen and (max-width:  390px)`]: {
              display: "inline",
              textOverflow: "ellipsis"
            }
          },
          children: "Searching..."
        }
      )
    ] }) : hasResults ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        Typography,
        {
          component: "h2",
          fontSize: 24,
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          sx: {
            [`@media screen and (max-width:  390px)`]: {
              display: "inline",
              textOverflow: "ellipsis"
            }
          },
          children: [
            "Search results for “",
            /* @__PURE__ */ jsx(Box, { component: "span", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", children: searchText }),
            "”"
          ]
        }
      ),
      /* @__PURE__ */ jsx(Button, { sx: { height: "auto", whiteSpace: "nowrap" }, onClick: () => setFilterDialogOpen(true), children: "Filter" })
    ] }) : /* @__PURE__ */ jsxs("div", { className: "headerText", children: [
      "No search results were returned for “",
      searchText,
      "”. Please try another query."
    ] }) }),
    /* @__PURE__ */ jsx(
      SearchProductGrid,
      {
        onShareClick,
        loading,
        LinkComponent,
        products: popularProducts,
        children: mostPopularBrand && /* @__PURE__ */ jsx(
          ProductBrandCard,
          {
            brand: mostPopularBrand,
            joinedState,
            reward: ((_c = campaignRewards[0]) == null ? void 0 : _c.rewardAmount) || 0,
            joined: mostPopularBrand.isMember,
            onJoin: handleJoin,
            LinkComponent
          }
        )
      }
    ),
    hasResults && (!disableBack || !disableNext) ? /* @__PURE__ */ jsxs(PaginationContainer, { children: [
      /* @__PURE__ */ jsx(IconButton, { disableRipple: true, disableFocusRipple: true, onClick: movePrev, disabled: disableBack, children: /* @__PURE__ */ jsx(
        ChevronLeftIcon,
        {
          sx: (theme) => ({ color: theme.vars.palette.text.primary, opacity: disableBack ? 0.5 : 1 })
        }
      ) }),
      /* @__PURE__ */ jsx(IconButton, { disableRipple: true, disableFocusRipple: true, onClick: moveNext, disabled: disableNext, children: /* @__PURE__ */ jsx(
        ChevronRightIcon,
        {
          sx: (theme) => ({ color: theme.vars.palette.text.primary, opacity: disableNext ? 0.5 : 1 })
        }
      ) })
    ] }) : null
  ] }) });
};
export {
  SearchResults
};
//# sourceMappingURL=SearchResults.js.map
